import { InputBase, Popper, Typography, ClickAwayListener } from '@mui/material'
import { styled } from '@mui/system'
import { colorPalette } from '../../../../../style/colorPalette'
import { Checkbox } from '../../../../../components/inputs/Checkbox'
import { SelectOption } from '../../../../../model/selectOption'
import { Button } from '../../../../../components/buttons'
import { intl } from '../../../../../../i18n'

type Props = {
  open: boolean
  anchorEl: Element | undefined
  options: SelectOption[]
  temporarySelection: Set<string>
  searchText: string
  onSearchTextChange: (text: string) => void
  onToggleItem: (value: string) => void
  onApply: () => void
  onClose: () => void
}

const ProcessScopeSearchSelectPopper = ({
  open,
  anchorEl,
  options,
  temporarySelection,
  searchText,
  onSearchTextChange,
  onToggleItem,
  onApply,
  onClose,
}: Props) => {
  return (
    <Popper
      open={open}
      anchorEl={anchorEl}
      sx={{
        zIndex: 1301,
        background: colorPalette.monotone[0],
        boxShadow:
          'rgb(0 0 0 / 20%) 0px 5px 5px -3px, rgb(0 0 0 / 14%) 0px 8px 10px 1px, rgb(0 0 0 / 12%) 0px 3px 14px 2px',
        borderRadius: '4px',
        padding: '10px',
        minWidth: '445px',
      }}
      placement="bottom-start"
      modifiers={[
        {
          name: 'offset',
          options: { offset: [0, 20] },
        },
        {
          name: 'flip',
          enabled: true,
          options: {
            altBoundary: true,
            rootBoundary: 'document',
            padding: 8,
          },
        },
        {
          name: 'preventOverflow',
          enabled: true,
          options: {
            altAxis: true,
            altBoundary: true,
            tether: true,
            rootBoundary: 'document',
            padding: 8,
          },
        },
      ]}
      disablePortal={true}
    >
      <ClickAwayListener onClickAway={onClose}>
        <PopperContent>
          <InputBase
            autoFocus={true}
            placeholder={intl.formatMessage({
              id: 'wbsItemSearchFilterPanel.processScope.searchPlaceholder',
            })}
            value={searchText}
            onChange={e => onSearchTextChange(e.target.value)}
            sx={{
              border: `1px solid ${colorPalette.monotone[2]}`,
              borderRadius: '4px',
              padding: '4px 8px',
              marginBottom: '8px',
              fontSize: '14px',
            }}
          />
          <OptionsList>
            {options.map(option => (
              <OptionItem
                key={option.value}
                onClick={() => onToggleItem(option.value)}
              >
                <Checkbox
                  sx={{ marginRight: '8px' }}
                  checked={temporarySelection.has(option.value)}
                />
                {option.iconUrl && <img src={option.iconUrl} />}
                <Typography
                  sx={{
                    margin: 'auto 10px',
                    color: colorPalette.monotone[4],
                  }}
                >
                  {option.name}
                </Typography>
              </OptionItem>
            ))}
          </OptionsList>
          <ApplyButtonContainer>
            <Button
              colorPattern="skyBlue"
              variant="filled"
              size="s"
              onClick={onApply}
            >
              {intl.formatMessage({
                id: 'savedUIState.content.button.apply',
              })}
            </Button>
          </ApplyButtonContainer>
        </PopperContent>
      </ClickAwayListener>
    </Popper>
  )
}

const PopperContent = styled('div')({
  display: 'flex',
  flexDirection: 'column',
})

const OptionsList = styled('div')({
  maxHeight: '300px',
  overflowY: 'auto',
})

const OptionItem = styled('div')({
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  cursor: 'pointer',
  '&:hover': {
    background: colorPalette.monotone[1],
  },
  borderRadius: '4px',
  padding: '2px 0',
})

const ApplyButtonContainer = styled('div')({
  display: 'flex',
  justifyContent: 'flex-end',
  marginTop: '8px',
})

export default ProcessScopeSearchSelectPopper
